/**
 * Marker Layer Configuration
 *
 * Provides individual sensor markers with color-coded moisture levels
 * using MapLibre GL JS circle and symbol layers.
 *
 * Features:
 * - Color-coded markers (brown=dry → green=optimal → blue=wet)
 * - Configurable marker sizes (small, medium, large)
 * - Smooth zoom-based opacity transition from heatmap
 * - Optional device ID labels above markers
 * - Hover and click interactions
 *
 * @see https://maplibre.org/maplibre-style-spec/layers/#circle
 */

import type { CircleLayerSpecification, SymbolLayerSpecification, ExpressionSpecification } from 'maplibre-gl';
import type { MarkerLayerConfig } from '../MapComponent.types';

// ============================================================================
// Color Configuration
// ============================================================================

/**
 * Moisture-based color interpolation expression
 * Maps moisture levels to a gradient from brown (dry) to blue (saturated)
 */
export const moistureColorExpression: ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['get', 'moisture'],
  0, '#B26640',    // brown (dry)
  30, '#DEB464',   // tan
  50, '#41AB5D',   // green (optimal)
  70, '#67A9CF',   // light blue
  100, '#2166AC', // blue (saturated)
];

/**
 * Static marker color (when colorByValue is false)
 */
export const defaultMarkerColor = '#3b82f6'; // Blue

// ============================================================================
// Size Configuration
// ============================================================================

/**
 * Marker size mappings in pixels
 */
export const markerSizeMap: Record<MarkerLayerConfig['size'], number> = {
  small: 6,
  medium: 10,
  large: 14,
};

/**
 * Zoom-interpolated marker radius expression
 * Markers grow larger as user zooms in
 */
export const getMarkerRadiusExpression = (baseSize: number): ExpressionSpecification => [
  'interpolate',
  ['linear'],
  ['zoom'],
  10, baseSize * 0.6,
  12, baseSize,
  15, baseSize * 1.3,
];

// ============================================================================
// Paint Configuration
// ============================================================================

/**
 * Generate marker circle paint configuration
 *
 * @param config - Marker layer configuration
 * @param enableZoomTransition - Whether to enable zoom-based opacity transition
 * @returns MapLibre circle paint specification
 */
export const getMarkerPaint = (
  config: MarkerLayerConfig,
  enableZoomTransition: boolean = true
): CircleLayerSpecification['paint'] => {
  const baseSize = markerSizeMap[config.size] || markerSizeMap.medium;

  // Opacity expression for smooth heatmap-to-marker transition
  const opacityExpression: ExpressionSpecification | number = enableZoomTransition
    ? ['interpolate', ['linear'], ['zoom'], 10, 0, 12, 0.9, 13, 1] as ExpressionSpecification
    : 0.9;

  return {
    'circle-radius': getMarkerRadiusExpression(baseSize),
    'circle-color': config.colorByValue ? moistureColorExpression : defaultMarkerColor,
    'circle-stroke-width': 2,
    'circle-stroke-color': '#ffffff',
    'circle-opacity': opacityExpression,
    'circle-stroke-opacity': opacityExpression,
  } as CircleLayerSpecification['paint'];
};

/**
 * Marker paint for selected/hovered state
 */
export const getMarkerHoverPaint = (
  config: MarkerLayerConfig
): CircleLayerSpecification['paint'] => {
  const baseSize = markerSizeMap[config.size] || markerSizeMap.medium;

  return {
    'circle-radius': baseSize * 1.3,
    'circle-color': config.colorByValue ? moistureColorExpression : defaultMarkerColor,
    'circle-stroke-width': 3,
    'circle-stroke-color': '#ffffff',
    'circle-opacity': 1,
  } as CircleLayerSpecification['paint'];
};

// ============================================================================
// Label Configuration
// ============================================================================

/**
 * Generate marker label paint configuration
 *
 * @returns MapLibre symbol paint specification for labels
 */
export const getMarkerLabelPaint = (): SymbolLayerSpecification['paint'] => ({
  'text-color': '#1f2937',
  'text-halo-color': '#ffffff',
  'text-halo-width': 1.5,
  'text-opacity': [
    'interpolate',
    ['linear'],
    ['zoom'],
    12, 0,
    14, 1,
  ] as ExpressionSpecification,
});

/**
 * Generate marker label layout configuration
 *
 * @returns MapLibre symbol layout specification for labels
 */
export const getMarkerLabelLayout = (): SymbolLayerSpecification['layout'] => ({
  'text-field': ['get', 'deviceId'] as ExpressionSpecification,
  'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
  'text-size': 10,
  'text-offset': [0, -1.5],
  'text-anchor': 'bottom',
  'text-allow-overlap': false,
  'text-ignore-placement': false,
});

// ============================================================================
// Layer IDs
// ============================================================================

export const MARKER_LAYER_ID = 'sensor-markers';
export const MARKER_LABEL_LAYER_ID = 'sensor-marker-labels';
export const MARKER_HOVER_LAYER_ID = 'sensor-markers-hover';

// ============================================================================
// Default Configuration
// ============================================================================

/**
 * Default marker layer configuration
 */
export const defaultMarkerConfig: MarkerLayerConfig = {
  enabled: false,
  showLabels: false,
  size: 'medium',
  colorByValue: true,
};

// ============================================================================
// Layer Specifications
// ============================================================================

/**
 * Get full marker layer specification
 *
 * @param sourceId - Source ID for the GeoJSON data
 * @param config - Marker layer configuration
 * @param heatmapEnabled - Whether heatmap is enabled (affects minzoom)
 * @returns Full circle layer specification for MapLibre
 */
export const getMarkerLayerSpec = (
  sourceId: string,
  config: MarkerLayerConfig,
  heatmapEnabled: boolean = true
): Omit<CircleLayerSpecification, 'source-layer'> => ({
  id: MARKER_LAYER_ID,
  type: 'circle',
  source: sourceId,
  paint: getMarkerPaint(config, heatmapEnabled),
  minzoom: heatmapEnabled ? 10 : 0,
});

/**
 * Get marker label layer specification
 *
 * @param sourceId - Source ID for the GeoJSON data
 * @returns Full symbol layer specification for labels
 */
export const getMarkerLabelLayerSpec = (
  sourceId: string
): Omit<SymbolLayerSpecification, 'source-layer'> => ({
  id: MARKER_LABEL_LAYER_ID,
  type: 'symbol',
  source: sourceId,
  paint: getMarkerLabelPaint(),
  layout: getMarkerLabelLayout(),
  minzoom: 13,
});

// ============================================================================
// Tooltip Data Formatting
// ============================================================================

/**
 * Format sensor data for tooltip display
 *
 * @param properties - Feature properties from GeoJSON
 * @returns Formatted tooltip data
 */
export const formatTooltipData = (properties: Record<string, unknown>): {
  deviceId: string;
  moisture: string;
  temperature?: string;
  zone?: string;
  timestamp?: string;
} => {
  const moisture = typeof properties.moisture === 'number'
    ? `${properties.moisture.toFixed(1)}%`
    : 'N/A';

  const temperature = typeof properties.temperature === 'number'
    ? `${properties.temperature.toFixed(1)}°`
    : undefined;

  const timestamp = properties.timestamp
    ? new Date(properties.timestamp as string).toLocaleString()
    : undefined;

  return {
    deviceId: String(properties.deviceId || 'Unknown'),
    moisture,
    temperature,
    zone: properties.zone as string | undefined,
    timestamp,
  };
};

/**
 * Get moisture status label
 *
 * @param moistureLevel - Moisture percentage (0-100)
 * @returns Status label (Dry, Optimal, Wet, Saturated)
 */
export const getMoistureStatus = (moistureLevel: number): {
  label: string;
  color: string;
} => {
  if (moistureLevel < 20) {
    return { label: 'Very Dry', color: '#B26640' };
  } else if (moistureLevel < 35) {
    return { label: 'Dry', color: '#DEB464' };
  } else if (moistureLevel < 65) {
    return { label: 'Optimal', color: '#41AB5D' };
  } else if (moistureLevel < 80) {
    return { label: 'Wet', color: '#67A9CF' };
  } else {
    return { label: 'Saturated', color: '#2166AC' };
  }
};

export default {
  getMarkerPaint,
  getMarkerLayerSpec,
  getMarkerLabelLayerSpec,
  formatTooltipData,
  getMoistureStatus,
  moistureColorExpression,
  defaultMarkerConfig,
  MARKER_LAYER_ID,
  MARKER_LABEL_LAYER_ID,
  markerSizeMap,
};
